import { Dialog } from "@progress/kendo-react-dialogs";
import { useState, useCallback, useEffect } from "react";
import moment from "moment";
import DropDownKendoRct from "src/control-components/drop-down/drop-down";
import { TableRowsLoader } from "src/control-components/custom-skelton";
import CommonExpansionPanel from "src/control-components/common-expansion-panel/common-expansion-panel";
import { setExpandedState } from "@progress/kendo-react-data-tools";

interface TreatmentPlanVersionCompareProps {
    onClose: () => void;
    treatmenTPlanAuditLogList?: treatmentPlanAuditLogData[],
    previousOne?: any,
    currentOne?: any
}

interface treatmentPlanAuditLogData {
    id: number
    treatmentPlanId: number
    currentVersionData: string
    versionNumber: string
    createdBy: number
    parentId: any
    clientId: number
    operatedBy: string
    signedBy: any
    signedDate: any
    utcDateCreated: string
    actionName: string
    actionId: number
}


interface FormateServiceProps {
    id: number | null
    displayText: string,
    parsedData: any[]


}

const ServiceHistoryVersionCompare: React.FC<TreatmentPlanVersionCompareProps> = ({ onClose, treatmenTPlanAuditLogList, previousOne, currentOne }) => {
    const [previousData, setPreviousData] = useState<FormateServiceProps>({
        displayText: "",
        parsedData: previousOne?.currentVersionData ? JSON.parse(previousOne.currentVersionData) : [],
        id: previousOne?.id
    });


    const [currentData, setCurrentData] = useState<FormateServiceProps>({
        displayText: "",
        parsedData: currentOne?.currentVersionData ? JSON.parse(currentOne.currentVersionData) : [],
        id: currentOne?.id
    });

    const getExpandedState = (dataArray?: any[], isSite: boolean = false, isPayer: boolean = false) => {
        if(isSite) {
            return dataArray?.reduce((acc, item) => {
                acc[item.siteId] = true;
                return acc;
            }, {} as { [key: string]: boolean }) || {};
        }
        else if(isPayer) {
            return dataArray?.reduce((acc, item) => {
                acc[item.payerId] = true;
                return acc;
            }, {} as { [key: string]: boolean }) || {};
        }
        else {
            return dataArray?.reduce((acc, item) => {
                acc[item.id] = true;
                return acc;
            }, {} as { [key: string]: boolean }) || {};
        }
    };

    const [expandedService, setExpandedService] = useState<{ [key: string]: boolean }>({});
    const [formattedAuditLogList, setFormattedAuditLogList] = useState<{ id: string; currentVersion: string; displayText: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [changeElements, setChangeElements] = useState<HTMLElement[]>([]);
    const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
    const [isDataReady, setIsDataReady] = useState(false);

    useEffect(() => {
        if (previousOne && currentOne) {
            setIsDataReady(true);
        }
    }, []);


    useEffect(() => {
        if ((Object.keys(currentData?.parsedData)?.length > 0) || (Object.keys(previousData?.parsedData?.length > 0))) {
            findChangeElements();
        }
    }, [currentData, previousData, isDataReady]);

    useEffect(() => {
        const updatedList: any = treatmenTPlanAuditLogList?.map((item: any) => ({
            id: item.id,
            currentVersionData: item?.currentVersionData,
            displayText: `Version:  v${item?.versionNumber}`
        }));

        setFormattedAuditLogList(updatedList);
    }, [treatmenTPlanAuditLogList]);

    useEffect(() => {
        if (Object.keys(currentData?.parsedData)?.length > 0 || Object.keys(previousData?.parsedData)?.length > 0) {
            const previousParsedData: any = Object.keys(previousData?.parsedData).length > 0 ? previousData.parsedData : {};
            const currentParsedData: any = Object.keys(currentData?.parsedData).length > 0 ? currentData.parsedData : {};
    
            // Construct service-level array manually
            const allServices = [
                { id: previousParsedData?.id },
                { id: currentParsedData?.id }
            ];
    
            setExpandedService(getExpandedState(allServices));
        }
    }, [currentData, previousData]);
    

    const findChangeElements = () => {
        const elements: any = document.querySelectorAll(".text-danger-light-tp, .text-success-light-tp, .tp-top,.tp-bottom");
        setChangeElements(elements);
    };

    const toggleService = useCallback((id: string) => { setExpandedService((prev) => ({ ...prev, [id]: !prev[id] })); }, []);

    const getComparisonClassCommon = (prevValue: any, currValue: any, isPrevious: boolean) => {
        if (prevValue !== currValue) {
            return isPrevious === true ? "text-danger-light-tp" : "text-success-light-tp";
        }
        return "";
    };


    const handlePreviousVersionChange = (e: any) => {
        if (!e?.value) return;

        setLoading(true);

        setTimeout(() => {
            const { currentVersionData, displayText, id } = e.value;
            const parsedData = currentVersionData ? JSON.parse(currentVersionData) : {};

            setPreviousData({ parsedData, displayText, id });

            setLoading(false);
        }, 300);
    };

    const handleCurrentVersionChange = (e: any) => {
        if (!e?.value) return;

        setLoading(true);

        setTimeout(() => {
            const { currentVersionData, displayText, id } = e.value;
            const parsedData = currentVersionData ? JSON.parse(currentVersionData) : {};

            setCurrentData({ parsedData, displayText, id });

            setLoading(false);
        }, 300);
    };

    const getSignatureComparisonClass = (prevSignatures: any[], currSignatures: any[], signObj: any) => {
        const prevHas = prevSignatures.some((sign) => sign.id === signObj.id);
        const currHas = currSignatures.some((sign) => sign.id === signObj.id);

        if (!prevHas && currHas) return "text-success-light-tp";
        if (prevHas && !currHas) return "text-danger-light-tp";
        return "";
    };

    const goToNextChange = () => {
        if (changeElements.length === 0) return;

        const viewportHeight = window.innerHeight;
        let nextElement: HTMLElement | null = null;

        for (let i = 0; i < changeElements.length; i++) {
            const element = changeElements[i] as HTMLElement;
            const rect = element.getBoundingClientRect();

            if (rect.top >= viewportHeight || rect.bottom > viewportHeight) {
                nextElement = element;
                setCurrentChangeIndex(i);
                break;
            }
        }

        if (nextElement) {
            nextElement.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
            window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        }
    };

    const currencyFormat = (num) => {
        const sign = num < 0 ? "-" : "";
        const formattedNumber = Math.abs(num)
          .toFixed(2)
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
        return `${sign}$${formattedNumber}`;
    };

    const goToPreviousChange = () => {
        if (changeElements.length === 0) return;

        let prevElement: HTMLElement | null = null;

        for (let i = changeElements.length - 1; i >= 0; i--) {
            const element = changeElements[i] as HTMLElement;
            const rect = element.getBoundingClientRect();

            if (rect.bottom <= 0) {
                prevElement = element;
                setCurrentChangeIndex(i);
                break;
            }
        }

        if (prevElement) {
            prevElement.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

        const renderCommonTpDetails = (prevItem: any, currItem: any, tpCommonItem: any, isPrevious: boolean) => {
            return (
                <>
                    <div className="d-flex justify-content-between py-2 align-items-center"></div>
                    <div className="date-timeshow px-3">
                        <div className="d-flex flex-wrap gapVertical">
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.dateEffective, currItem?.dateEffective, isPrevious)}`}>
                                    <b className="plan-items-head">Date Effective: </b>
                                    <span className="plan-values">
                                        {tpCommonItem.dateEffective ? moment(tpCommonItem.dateEffective).format("M/D/YYYY") : ""}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.dateEnd, currItem?.dateEnd, isPrevious)}`}>
                                    <b className="plan-items-head">Date End: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.dateEnd ? moment(tpCommonItem.dateEnd).format("M/D/YYYY") : "-/-/-"}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.serviceCode, currItem?.serviceCode, isPrevious)}`}>
                                    <b className="plan-items-head">Service Code: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.serviceCode}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.billingUnitsDesc, currItem?.billingUnitsDesc, isPrevious)}`}>
                                    <b className="plan-items-head">Billing Unit: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.billingUnitsDesc}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.modifier, currItem?.modifier, isPrevious)}`}>
                                    <b className="plan-items-head">Modifier 1: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.modifier}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.secModifier, currItem?.secModifier, isPrevious)}`}>
                                    <b className="plan-items-head">Modifier 2: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.secModifier}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.thirdModifier, currItem?.thirdModifier, isPrevious)}`}>
                                    <b className="plan-items-head">Modifier 3: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.thirdModifier}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.fourthModifier, currItem?.fourthModifier, isPrevious)}`}>
                                    <b className="plan-items-head">Modifier 4: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.fourthModifier}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.revenueCode, currItem?.revenueCode, isPrevious)}`}>
                                    <b className="plan-items-head">Revenue Code: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.revenueCode}
                                    </span>
                                </p>
                            </div>
                            
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.maxTime, currItem?.maxTime, isPrevious)}`}>
                                    <b className="plan-items-head">Max Time: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.maxTime}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.minTime, currItem?.minTime, isPrevious)}`}>
                                    <b className="plan-items-head">Min Time: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.minTime}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.billable, currItem?.billable, isPrevious)}`}>
                                    <b className="plan-items-head">Billable: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.billable ? "Yes" : "No"}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.allowOverlapping, currItem?.allowOverlapping, isPrevious)}`}>
                                    <b className="plan-items-head">AllowOverlapping: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.allowOverlapping ? "Yes" : "No"}
                                    </span>
                                </p>
                            </div>

                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.authRequired, currItem?.authRequired, isPrevious)}`}>
                                    <b className="plan-items-head">Auth Required: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.authRequired ? "Yes" : "No"}
                                    </span>
                                </p>
                            </div>

                        </div>
                    </div>
                </>
            )
        }

        const renderServiceRateDetails = (prevItem: any, currItem: any, tpCommonItem: any, isPrevious: boolean) => {
            return (
                <>
                    <div className="d-flex justify-content-between py-2 align-items-center"></div>
                    <div className="date-timeshow px-3">
                        <div className="d-flex flex-wrap gapVertical">
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.dateEffective, currItem?.dateEffective, isPrevious)}`}>
                                    <b className="plan-items-head">Date Effective: </b>
                                    <span className="plan-values">
                                        {tpCommonItem.dateEffective ? moment(tpCommonItem.dateEffective).format("M/D/YYYY") : ""}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-3 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.dateEnd, currItem?.dateEnd, isPrevious)}`}>
                                    <b className="plan-items-head">Date End: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.dateEnd ? moment(tpCommonItem.dateEnd).format("M/D/YYYY") : "-/-/-"}
                                    </span>
                                </p>
                            </div>
                            <div className="col-md-3 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.serviceRate, currItem?.serviceRate, isPrevious)}`}>
                                    <b className="plan-items-head">Service Rate: </b>
                                    <span className={"plan-values"}>
                                        {tpCommonItem?.serviceRate ? currencyFormat(tpCommonItem?.serviceRate) : 'N/A'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )
        }

        const renderServiceSiteDetails = (prevItem: any, currItem: any, tpCommonItem: any, isPrevious: boolean) => {
            return (
                <>
                    <div className="d-flex justify-content-between py-2 align-items-center"></div>
                    <div className="date-timeshow px-3">
                        <div className="d-flex flex-wrap gapVertical">
                            <div className="col-md-4 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.siteName, currItem?.siteName, isPrevious)}`}>
                                    <span className="plan-values">
                                        {tpCommonItem?.siteName}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )
        }

        const renderServicePosDetails = (prevItem: any, currItem: any, tpCommonItem: any, isPrevious: boolean) => {
            return (
                <>
                    <div className="d-flex justify-content-between py-2 align-items-center"></div>
                    <div className="date-timeshow px-3">
                        <div className="d-flex flex-wrap gapVertical">
                            <div className="col-md-9 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.name, currItem?.name, isPrevious)}`}>
                                    <span className="plan-values">
                                        {tpCommonItem?.name}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )
        }

        const renderServicePayerDetails = (prevItem: any, currItem: any, tpCommonItem: any, isPrevious: boolean) => {
            return (
                <>
                    <div className="d-flex justify-content-between py-2 align-items-center"></div>
                    <div className="date-timeshow px-3">
                        <div className="d-flex flex-wrap gapVertical">
                            <div className="col-md-9 col-sm-12 p-0">
                                <p className={`mb-2 ${getComparisonClassCommon(prevItem?.payerName, currItem?.payerName, isPrevious)}`}>
                                    <span className="plan-values">
                                        {tpCommonItem?.payerName}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </>
            )
        }



    return (
        <Dialog
            onClose={onClose}
            title={
                <div className="d-flex align-items-center w-100">
                    <span className="mr-4">View Changes</span>
                    <div>
                        <button className='btn-transparent' onClick={goToPreviousChange} style={{ color: "#07479a", fontWeight: "bold" }}
                        ><i className="fa-solid fa-chevron-left" ></i>  Prev</button>
                        <button className='btn-transparent' onClick={goToNextChange} style={{ color: "#07479a", fontWeight: "bold" }}
                        >Next <i className="fa-solid fa-chevron-right" style={{ color: "#07479a" }}></i></button>
                    </div>
                </div>
            }
            className="pagination-row-cus blue_theme"
            width="100%"
            height="90%"
        >
            <div>
                <div className="row">
                    <div className="col-lg-6 col-md-6 col-12">
                        <DropDownKendoRct
                            label="Select a Version to Compare"
                            onChange={handlePreviousVersionChange}
                            data={formattedAuditLogList}
                            value={formattedAuditLogList?.find(
                                (item) => Number(item.id) === previousData?.id
                            ) || null}
                            textField="displayText"
                            name="displayText"
                            dataItemKey="id"
                        />
                    </div>
    
                    <div className="col-lg-6 col-md-6 col-12">
                        <DropDownKendoRct
                            label="Select a Version to Compare"
                            onChange={handleCurrentVersionChange}
                            data={formattedAuditLogList}
                            value={formattedAuditLogList?.find(
                                (item) => Number(item.id) === currentData?.id
                            ) || null}
                            textField="displayText"
                            name="displayText"
                            dataItemKey="id"
                        />
                    </div>
                </div>
    
                {(Object.keys(previousData?.parsedData || {}).length > 0 &&
                    Object.keys(currentData?.parsedData || {}).length > 0) ? (
                    <>
                        {loading ? (
                            <TableRowsLoader rowsNum={12} />
                        ) : (
                            (() => {
                                const currItem: any = currentData?.parsedData;
                                const prevItem: any = previousData?.parsedData;
    
                                return (
                                    <div className="row" key={prevItem.id}>
                                        <div className="col-md-6">
                                            <CommonExpansionPanel
                                                id={`${prevItem?.id}-prev`}
                                                title={
                                                    <span
                                                        className={`tp-top ${getComparisonClassCommon(
                                                            prevItem?.service,
                                                            currItem?.service,
                                                            true
                                                        )}`}
                                                        style={{ color: "black", fontWeight: "bold" }}
                                                    >
                                                        {prevItem?.service}
                                                    </span>
                                                }
                                                expanded={!!expandedService[prevItem?.id]}
                                                onAction={() => toggleService(prevItem?.id)}
                                            >
                                            {renderCommonTpDetails(prevItem, currItem, prevItem, true)}
             
                                            {prevItem?.serviceRateList?.length > 0 && (
                                                <>
                                                    <h4 className="f-20 address-title my-3 ml-3">Service Rates</h4>
                                                    <ul className="ml-4">
                                                    {prevItem?.serviceRateList?.map((prevRate: any) => {
                                                        const currRate = currItem?.serviceRateList?.find((g: any) => g?.id === prevRate?.id) || {};
                                                        return (
                                                        <li key={`${prevRate?.id}-prev`}>
                                                            {renderServiceRateDetails(prevRate, currRate, prevRate, true)}

                                                            {prevRate?.payers?.length > 0 && (
                                                            <>
                                                                <h4 className="f-20 address-title my-3 ml-3 pl-2">Payers</h4>
                                                                <ul className="ml-5">
                                                                {prevRate?.payers?.map((prevPayer: any) => {
                                                                    const currPayer = currRate?.payers?.find((g: any) => g?.payerId === prevPayer?.payerId) || {};
                                                                    return (
                                                                    <li key={`${prevPayer?.payerId}-prev`}>
                                                                        {renderServicePayerDetails(prevPayer, currPayer, prevPayer, true)}
                                                                    </li>
                                                                    );
                                                                })}
                                                                </ul>
                                                            </>
                                                            )}
                                                        </li>
                                                        );
                                                    })}
                                                    </ul>
                                                </>
                                            )}

                                            {prevItem?.serviceSites?.length > 0 && <h4 className="f-20 address-title my-3 ml-3">Service Sites</h4>}
                                            <ul>
                                                {prevItem?.serviceSites?.map((prevSite: any) => {
                                                    const currSite = currItem?.serviceSites?.find((g: any) => g?.siteId === prevSite?.siteId) || {};
                                                    return (
                                                    <li key={`${prevSite?.siteId}-prev`}>
                                                        {renderServiceSiteDetails(prevSite, currSite, prevSite, true)}
                                                    </li>
                                                    );
                                                })}
                                            </ul>

                                            {prevItem?.posList?.length > 0 && <h4 className="f-20 address-title my-3 ml-3">POS</h4>}
                                            <ul>
                                                {prevItem?.posList?.map((prevPos: any) => {
                                                    const currPos = currItem?.posList?.find((g: any) => g?.id === prevPos?.id) || {};
                                                    return (
                                                    <li key={`${prevPos?.id}-prev`}>
                                                        {renderServicePosDetails(prevPos, currPos, prevPos, true)}
                                                    </li>
                                                    );
                                                })}
                                            </ul>

                                            </CommonExpansionPanel>
                                        </div>


                                        <div className="col-md-6">
                                            <CommonExpansionPanel
                                                id={`${currItem?.id}-curr`}
                                                title={
                                                    <span
                                                        className={`tp-top ${getComparisonClassCommon(
                                                            prevItem?.service,
                                                            currItem?.service,
                                                            false
                                                        )}`}
                                                        style={{ color: "black", fontWeight: "bold" }}
                                                    > 
                                                        {currItem?.service}
                                                    </span>
                                                }
                                                expanded={!!expandedService[currItem?.id]}
                                                onAction={() => toggleService(currItem?.id)}
                                            >
                                            {renderCommonTpDetails(prevItem, currItem, currItem, false)}

                                            {currItem?.serviceRateList?.length > 0 && (
                                                <>
                                                    <h4 className="f-20 address-title my-3 ml-3">Service Rates</h4>
                                                    <ul className="ml-4">
                                                    {currItem?.serviceRateList?.map((currRate: any) => {
                                                        const prevRate = prevItem?.serviceRateList?.find((g: any) => g?.id === currRate?.id) || {};
                                                        return (
                                                        <li key={`${currRate?.id}-curr`}>
                                                            {renderServiceRateDetails(prevRate, currRate, currRate, false)}

                                                            {/* Payers section under each service rate, if available */}
                                                            {currRate?.payers?.length > 0 && (
                                                            <>
                                                                <h4 className="f-20 address-title my-3 ml-3 pl-2">Payers</h4>
                                                                <ul className="ml-5">
                                                                {currRate?.payers?.map((currPayer: any) => {
                                                                    const prevPayer = prevRate?.payers?.find((g: any) => g?.payerId === currPayer?.payerId) || {};
                                                                    return (
                                                                    <li key={`${currPayer?.payerId}-curr`}>
                                                                        {renderServicePayerDetails(prevPayer, currPayer, currPayer, false)}
                                                                    </li>
                                                                    );
                                                                })}
                                                                </ul>
                                                            </>
                                                            )}
                                                        </li>
                                                        );
                                                    })}
                                                    </ul>
                                                </>
                                            )}

                                              
                                           {currItem?.serviceSites?.length > 0 && <h4 className="f-20 address-title my-3 ml-3">Service Sites</h4>}
                                           <ul>
                                            {currItem?.serviceSites?.map((currSite: any) => {
                                                const prevSite = prevItem?.serviceSites?.find((g: any) => g?.siteId === currSite?.siteId) || {};
                                                return (
                                                <li key={`${currSite?.siteId}-curr`}>
                                                    {renderServiceSiteDetails(prevSite, currSite, currSite, false)}
                                                </li>
                                                );
                                            })}
                                          </ul>

                                        {currItem?.posList?.length > 0 && <h4 className="f-20 address-title my-3 ml-3">POS</h4>}
                                        <ul>
                                            {currItem?.posList?.map((currPos: any) => {
                                                const prevPos = prevItem?.posList?.find((g: any) => g?.id === currPos?.id) || {};
                                                return (
                                                <li key={`${currPos?.id}-curr`}>
                                                    {renderServicePosDetails(prevPos, currPos, currPos, false)}
                                                </li>
                                                );
                                            })}
                                          </ul>
                                            </CommonExpansionPanel>
                                        </div>
                                    </div>
                                );
                            })()
                        )}
                    </>
                ) : (
                    <div className="message-not-found col-12">
                        Unable to compare these two versions
                    </div>
                )}
            </div>
        </Dialog>
    );
    
};

export default ServiceHistoryVersionCompare;